import { useEffect, useRef, useState } from "react";
import {
  Award,
  Bookmark,
  BookOpen,
  Calculator,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Expand,
  Files,
  Layers,
  Lock,
  Menu,
  MonitorPlay,
  Printer,
  RotateCcw,
  X,
} from "lucide-react";
import { academyCourses, catalogCourses, academyPrograms } from "./data/courses";
import type { Course, Program } from "./types";
import { useLearningState } from "./hooks/useLearningState";
import type { AcademyState,LearningState,LessonBookmark,ResourceDocumentState } from "./hooks/useLearningState";
import { Assessment } from "./components/Assessment";
import { DecisionCalculator } from "./components/DecisionCalculator";
import { FinancialTools } from "./components/FinancialTools";
import { FinalCertification } from "./components/FinalCertification";
import { InstructorLayer } from "./components/instructor/InstructorLayer";
import {DecisionPause,GuidedPractice,IndependentApplication,ModelOutput,PostLessonPractice,PriorArtefactCallout,PracticalApplication} from "./components/PedagogyBlocks";
import {isDecisionPause,isGuidedPractice,isIndependentApplication,isModelContent,priorArtefacts} from "./components/pedagogy";
import { getOrientationInstructorCues } from "./data/instructorCues";
import { CustomerServiceResourceWorkspace } from "./components/CustomerServiceResourceWorkspace";
import { CustomerServiceVisual } from "./components/CustomerServiceVisual";
import { GenericLessonVisual } from "./components/GenericLessonVisual";
import { CourseCoverIcon, hasCourseCoverIcon } from "./components/CourseCoverIcon";
import { customerServiceResources, resourceById, resourcesForLesson } from "./data/customerServiceResources";
import { customerServiceVisualByLesson } from "./data/customerServiceVisuals";
import {
  adjacentLesson,
  findLessonLocation,
  lessonProgress,
  moduleIsComplete,
  requiredLessonIds,
} from "./learningEngine";

function App() {
  const reviewScreen = new URLSearchParams(location.search).get("review");
  const [activeCourseId, setActiveCourseId] = useState<string | null>(
    reviewScreen ? "project-finance-management" : null,
  );
  const [activeProgramId, setActiveProgramId] = useState<string | null>(null);
  const course =
    academyCourses.find((item) => item.id === activeCourseId) ??
    academyCourses[0];
  const {
    state,
    setState,
    resetProgress,
    devOverride,
    academy,
    recordCourseView,
    overallProgress,
    saveStatus,
  } = useLearningState(course.id);
  const [started, setStarted] = useState(Boolean(reviewScreen));
  const [nav, setNav] = useState(false);
  const navTriggerRef = useRef<HTMLButtonElement>(null);
  const [tools, setTools] = useState(reviewScreen === "tools");
  const [presenterSlide, setPresenterSlide] = useState(0);
  const [activeResourceId, setActiveResourceId] = useState<string | null>(null);
  const [slideCount, setSlideCount] = useState(1);
  const isSlidePresentation = course.id === "project-finance-management";
  const lessonLocation = findLessonLocation(
    course,
    state.activeModuleId,
    state.activeLessonId,
  );
  const module = course.modules[lessonLocation.moduleIndex];
  const lesson = module.lessons[lessonLocation.lessonIndex];
  const positionsRef = useRef(state.positions);
  positionsRef.current = state.positions;
  const progress = lessonProgress(course, state.completedLessons);
  const lessonResources = course.id === "professional-customer-service-client-experience"
    ? resourcesForLesson(lesson.id)
    : [];
  const activeResource = activeResourceId ? resourceById(activeResourceId) : undefined;
  const updateResource = (next: ResourceDocumentState) =>
    setState((current) => ({
      ...current,
      resourceDocuments: { ...current.resourceDocuments, [next.resourceId]: next },
    }));
  const allComplete = requiredLessonIds(course).every((id) =>
    state.completedLessons.includes(id),
  );
  const canAccess = (index: number) =>
    devOverride ||
    index === 0 ||
    state.completed.includes(course.modules[index - 1].id);
  const selectLesson = (moduleIndex: number, lessonId: string) => {
    if (!canAccess(moduleIndex)) return;
    const next = findLessonLocation(
      course,
      course.modules[moduleIndex].id,
      lessonId,
    );
    setState((s) => ({
      ...s,
      moduleIndex: next.moduleIndex,
      activeModuleId: next.moduleId,
      activeLessonId: next.lessonId,
    }));
  };
  const move = (delta: -1 | 1) => {
    const next = adjacentLesson(course, lesson.id, delta);
    if (next && canAccess(next.moduleIndex))
      selectLesson(next.moduleIndex, next.lessonId);
  };
  const slideElements = () =>
    Array.from(
      document.querySelectorAll<HTMLElement>(".lesson-content>*"),
    ).filter((element) => !element.classList.contains("slide-excluded"));
  const moveSlide = (delta: number) => {
    const slides = slideElements();
    const next = Math.max(
      0,
      Math.min(slides.length - 1, presenterSlide + delta),
    );
    setPresenterSlide(next);
    if (!isSlidePresentation)
      slides[next]?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (!started) return;
      const target=e.target instanceof Element?e.target:null;
      if(target?.closest('input,textarea,select,button,a,[contenteditable="true"],[role="button"],[role="radio"],[role="checkbox"]'))return;
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        if (state.mode === "presenter" || isSlidePresentation) moveSlide(1);
        else move(1);
      }
      if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        if (state.mode === "presenter" || isSlidePresentation) moveSlide(-1);
        else move(-1);
      }
      if (e.key === "Home" && isSlidePresentation) {
        e.preventDefault();
        setPresenterSlide(0);
      }
      if (e.key === "End" && isSlidePresentation) {
        e.preventDefault();
        setPresenterSlide(Math.max(0, slideElements().length - 1));
      }
      if (e.key === "Escape") setNav(false);
    };
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  });
  useEffect(() => {
    if (state.mode === "presenter" || isSlidePresentation) {
      setPresenterSlide(0);
    }
  }, [state.mode, lesson.id, isSlidePresentation, state.orientationComplete]);
  useEffect(() => {
    if (state.mode !== "presenter" && !isSlidePresentation) return;
    const root = document.getElementById("root");
    if (!root) return;
    const applySlides = () => {
      const slides = slideElements();
      setSlideCount(slides.length || 1);
      slides.forEach((element, index) => {
        const active = index === presenterSlide;
        element.classList.toggle("active-slide", active);
        element.toggleAttribute("hidden", !active);
      });
    };
    applySlides();
    const observer = new MutationObserver(applySlides);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [state.mode, isSlidePresentation, presenterSlide, lesson.id, state.orientationComplete]);
  useEffect(() => {
    if (!started) return;
    let timer = 0;
    const save = () => {
      clearTimeout(timer);
      timer = window.setTimeout(
        () =>
          setState((s) => ({
            ...s,
            positions: { ...s.positions, [lesson.id]: window.scrollY },
          })),
        180,
      );
    };
    if (!isSlidePresentation && !navigator.userAgent.includes("jsdom"))
      window.scrollTo({ top: positionsRef.current[lesson.id] ?? 0 });
    addEventListener("scroll", save, { passive: true });
    return () => {
      clearTimeout(timer);
      removeEventListener("scroll", save);
    };
  }, [lesson.id, started, setState, isSlidePresentation]);
  const complete = () =>
    setState((s) => {
      const completedLessons = Array.from(
        new Set([...s.completedLessons, lesson.id]),
      );
      const completed = moduleIsComplete(course, module.id, completedLessons)
        ? Array.from(new Set([...s.completed, module.id]))
        : s.completed;
      return { ...s, completedLessons, completed };
    });
  const isBookmarked = state.bookmarks.some(
    (item) =>
      item.courseId === course.id &&
      item.moduleId === module.id &&
      item.lessonId === lesson.id,
  );
  const cuesFor=(...modes:NonNullable<typeof lesson.instructorCues>[number]['mode'][])=>lesson.instructorCues?.filter(cue=>modes.includes(cue.mode));
  const decisionExamples=lesson.practicalExamples.filter(isDecisionPause);
  const guidedExamples=lesson.practicalExamples.filter(isGuidedPractice);
  const ordinaryExamples=lesson.practicalExamples.filter(item=>!isDecisionPause(item)&&!isGuidedPractice(item));
  const artefacts=priorArtefacts(JSON.stringify({sections:lesson.sections,examples:lesson.practicalExamples,exercise:lesson.exercise,homework:lesson.homework}));
  const isLastLesson=lessonLocation.lessonIndex===module.lessons.length-1;
  const bookmark = () =>
    setState((s) => ({
      ...s,
      bookmarks: isBookmarked
        ? s.bookmarks.filter(
            (item) =>
              !(
                item.courseId === course.id &&
                item.moduleId === module.id &&
                item.lessonId === lesson.id
              ),
          )
        : [
            ...s.bookmarks,
            { courseId: course.id, moduleId: module.id, lessonId: lesson.id },
          ],
    }));
  const fullscreen = () =>
    document.fullscreenElement
      ? document.exitFullscreen()
      : document.documentElement.requestFullscreen();
  useEffect(() => {
    if (activeCourseId) recordCourseView(activeCourseId);
  }, [activeCourseId, recordCourseView]);
  const exitToCourseList = () => {
    setActiveCourseId(null);
    setStarted(false);
  };
  if (!started || !activeCourseId) {
    const activeProgram = activeProgramId
      ? academyPrograms.find((p) => p.id === activeProgramId)
      : undefined;
    if (activeProgram)
      return (
        <ProgramView
          program={activeProgram}
          academy={academy}
          openCourse={(id) => {
            setActiveCourseId(id);
            setStarted(true);
          }}
          back={() => setActiveProgramId(null)}
        />
      );
    return (
      <AcademyHome
        academy={academy}
        overallProgress={overallProgress}
        openCourse={(id) => {
          setActiveCourseId(id);
          setStarted(true);
        }}
        openProgram={(id) => setActiveProgramId(id)}
      />
    );
  }
  if (course.availability === "research" && !devOverride)
    return <CourseFramework course={course} close={exitToCourseList} />;
  if (tools) return <FinancialTools onClose={() => setTools(false)} />;
  if (!state.orientationComplete && !reviewScreen)
    return (
      <Orientation
        course={course}
        onComplete={() =>
          setState((s) => ({ ...s, orientationComplete: true }))
        }
      />
    );
  return (
    <div className={`academy ${state.mode} ${isSlidePresentation ? "finance-slide-mode" : ""}`}>
      <a className="skip-link" href="#lesson-content">
        Skip to lesson content
      </a>
      <header className="topbar">
        <button
          ref={navTriggerRef}
          className="icon"
          onClick={() => setNav(true)}
          aria-label="Open module navigation"
        >
          <Menu />
        </button>
        <a
          className="brand"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            exitToCourseList();
          }}
        >
          <span>P</span>Pulkra Academy
        </a>
        <div className="course-label">
          <b>{course.title}</b>
          <small>
            {course.level} ·{" "}
            {saveStatus === "error"
              ? "Progress not saved"
              : saveStatus === "saving"
                ? "Saving…"
                : "Saved"}
          </small>
        </div>
        <div className="header-actions">
          <button className="text-button" onClick={() => setTools(true)}>
            <Calculator /> Tools
          </button>
          <button
            className="text-button"
            onClick={() =>
              setState((s) => ({
                ...s,
                mode: s.mode === "learner" ? "presenter" : "learner",
              }))
            }
          >
            <MonitorPlay /> {state.mode === "learner" ? "Presenter" : "Learner"}
          </button>
          <button
            className="icon"
            onClick={fullscreen}
            aria-label="Toggle fullscreen"
          >
            <Expand />
          </button>
        </div>
      </header>
      <div
        className="progress-track"
        role="progressbar"
        aria-label={`${progress}% course complete`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      {activeResource && (
        <div className="resource-overlay" role="dialog" aria-modal="true" aria-label={`${activeResource.title} workspace`}>
          <CustomerServiceResourceWorkspace
            resource={activeResource}
            document={state.resourceDocuments[activeResource.id]}
            onChange={updateResource}
            onClose={() => setActiveResourceId(null)}
            saveStatus={saveStatus}
          />
        </div>
      )}
      {(state.mode === "presenter" || isSlidePresentation) && (
        <div className="slide-indicator" aria-live="polite">
          Slide {presenterSlide + 1} of {slideCount}
          <button onClick={() => moveSlide(-1)} disabled={presenterSlide === 0}>
            <ChevronLeft />
          </button>
          <button
            onClick={() => moveSlide(1)}
            disabled={presenterSlide >= slideCount - 1}
          >
            <ChevronRight />
          </button>
        </div>
      )}
      <main id="lesson-content" className="lesson-shell">
        <aside className="lesson-meta">
          <p className="eyebrow">
            Module {module.number} of {course.modules.length} · Lesson{" "}
            {lessonLocation.lessonIndex + 1} of {module.lessons.length}
          </p>
          <h1>{module.title}</h1>
          <p>{module.promise}</p>
          <div className="meta-row">
            <span>{lesson.duration} min</span>
            <span>{lesson.title}</span>
          </div>
          <button
            className={`bookmark ${isBookmarked ? "active" : ""}`}
            onClick={bookmark}
          >
            <Bookmark /> {isBookmarked ? "Bookmarked" : "Bookmark lesson"}
          </button>
        </aside>
        <article className="lesson-content">
          <InstructorLayer
            key={`${course.id}-${lesson.id}`}
            cues={cuesFor('intro')}
            contextKey={`${course.id}-${module.id}-${lesson.id}`}
          />
          <section className="hero-card">
            <p className="eyebrow">Executive summary</p>
            <h2>{lesson.title}</h2>
            <p>{lesson.executiveSummary}</p>
          </section>
          <section>
            <h2>Learning outcomes</h2>
            <ul className="outcomes">
              {lesson.outcomes.map((x) => (
                <li key={x}>
                  <Check />
                  {x}
                </li>
              ))}
            </ul>
          </section>
          <PriorArtefactCallout items={artefacts}/>
          {lesson.sections.map((s, i) => (
            <section
              id={
                module.id === "financial-mindset" && i === 0
                  ? "teaching-profit-is-not-cash"
                  : undefined
              }
              key={s.heading}
              className={`teaching ${isModelContent(s.heading,s.body)?'teaching-model':''}`}
            >
              <span className="section-number">0{i + 1}</span>
              <div>
                <h2>{s.heading}</h2>
                {isModelContent(s.heading,s.body)?<ModelOutput title={s.heading} body={s.body}/>:<p>{s.body}</p>}
                {s.steps && (
                  <ol>
                    {s.steps.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ol>
                )}
              </div>
            </section>
          ))}
          <InstructorLayer cues={cuesFor('explanation')} contextKey={`${course.id}-${lesson.id}-explain`} showControls={false} compact/>
          {decisionExamples.map(text=><DecisionPause key={text} text={text} reasoning={lesson.workedExample.decision}/>)}
          {guidedExamples.map(text=><GuidedPractice key={text} text={text}/>)}
          {ordinaryExamples.length>0&&<section><h2>Practical applications</h2><div className="application-grid">{ordinaryExamples.map(text=><PracticalApplication key={text} text={text}/>)}</div></section>}
          <InstructorLayer cues={cuesFor('activity')} contextKey={`${course.id}-${lesson.id}-challenge`} showControls={false} compact/>
          <WorkedExample />
          <InstructorLayer cues={cuesFor('worked-example')} contextKey={`${course.id}-${lesson.id}-demonstrate`} showControls={false} compact/>
          <VisualCard />
          <section className="split">
            <div>
              <h2>Common mistakes</h2>
              <ul>
                {lesson.commonMistakes.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
            <div className="best">
              <h2>Best practice</h2>
              <ul>
                {lesson.bestPractices.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          </section>
          <section id="module-practical-exercise" className="exercise">
            {isIndependentApplication(lesson.exercise)?<IndependentApplication text={lesson.exercise}/>:<><p className="eyebrow">Practical exercise</p><h2>Turn insight into operating evidence</h2><p>{lesson.exercise}</p></>}
            <button onClick={() => window.print()}><Printer /> Print worksheet</button>
            {lessonResources.map((resource) => (
              <button className="resource-launch" key={resource.id} onClick={() => setActiveResourceId(resource.id)}>
                <Files /> {state.resourceDocuments[resource.id] ? "Continue resource" : "Open resource"}: {resource.title}
              </button>
            ))}
          </section>
          {lesson.homework&&<PostLessonPractice text={lesson.homework}/>} 
          <InstructorLayer cues={cuesFor('summary')} contextKey={`${course.id}-${lesson.id}-debrief`} showControls={false} compact/>
          <section>
            <h2>Reflection</h2>
            {lesson.reflection.map((x, i) => (
              <label className="reflection" key={x}>
                <span>0{i + 1}</span>
                <textarea
                  aria-label={x}
                  placeholder={x}
                  value={state.reflections[lesson.id]?.[x] ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    setState((s) => ({
                      ...s,
                      reflections: {
                        ...s.reflections,
                        [lesson.id]: {
                          ...s.reflections[lesson.id],
                          [x]: value,
                        },
                      },
                    }));
                  }}
                />
              </label>
            ))}
          </section>
          <section className="takeaways">
            <p className="eyebrow">Key takeaways</p>
            {lesson.takeaways.map((x) => (
              <p key={x}>{x}</p>
            ))}
          </section>
          <section className="lesson-summary">
            <p className="eyebrow">Lesson summary</p>
            <h2>The operating standard</h2>
            <p>{lesson.summary}</p>
            {lesson.references && (
              <details>
                <summary>Professional references</summary>
                <ul>
                  {lesson.references.map((reference) => (
                    <li key={reference}>{reference}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>
          {course.id === "project-finance-management" && <DecisionCalculator />}
          {isLastLesson&&<section id="module-conclusion" className="module-conclusion">
            <p className="eyebrow">Module conclusion</p>
            <h2>Apply the control in a real operating context</h2>
            <div className="conclusion-grid">
              <div>
                <h3>Practical assignment</h3>
                <p>{module.assignment}</p>
              </div>
              <div>
                <h3>Case study</h3>
                <p>{module.caseStudy}</p>
              </div>
            </div>
            <h3>Review checklist</h3>
            <ul className="review-list">
              {module.reviewChecklist.map((x) => (
                <li key={x}>
                  <Check />
                  {x}
                </li>
              ))}
            </ul>
          </section>}
          <InstructorLayer cues={cuesFor('quiz-intro')} contextKey={`${course.id}-${lesson.id}-assessment`} showControls={false} compact/>
          <Assessment
            key={lesson.id}
            questions={lesson.questions}
            onPass={complete}
            passScore={
              course.id === "project-finance-management" &&
              module.id === "financial-mindset"
                ? 80
                : 70
            }
          />
          {allComplete &&
            (course.id === "project-finance-management" ? (
              <FinalCertification
                passed={state.finalExamPassed}
                onPass={() =>
                  setState((s) => ({ ...s, finalExamPassed: true }))
                }
                name={state.certificateName}
                setName={(certificateName) =>
                  setState((s) => ({ ...s, certificateName }))
                }
              />
            ) : (
              <StandardCourseCompletion
                course={course}
                name={state.certificateName}
                setName={(certificateName) =>
                  setState((s) => ({ ...s, certificateName }))
                }
              />
            ))}
          {state.mode === "presenter" && (
            <section className="notes slide-excluded">
              <h2>Speaker notes</h2>
              <p>{lesson.speakerNotes}</p>
            </section>
          )}
        </article>
      </main>
      <footer className="lesson-footer">
        <button
          onClick={() => {
            if (isSlidePresentation && presenterSlide > 0) moveSlide(-1);
            else move(-1);
          }}
          disabled={isSlidePresentation ? presenterSlide === 0 && !adjacentLesson(course, lesson.id, -1) : !adjacentLesson(course, lesson.id, -1)}
        >
          <ChevronLeft /> Previous{isSlidePresentation ? "" : module.lessons.length > 1 ? " lesson" : ""}
        </button>
        <div>
          <small>{progress}% complete</small>
          <b>{lesson.title}</b>
        </div>
        <button
          className={
            state.completedLessons.includes(lesson.id) ? "completed" : ""
          }
          onClick={() => {
            if (isSlidePresentation && presenterSlide < slideCount - 1) {
              moveSlide(1);
              return;
            }
            if (state.completedLessons.includes(lesson.id)) move(1);
            else {
              const assessmentIndex = slideElements().findIndex((element) => element.id === "module-assessment");
              if (isSlidePresentation && assessmentIndex >= 0) setPresenterSlide(assessmentIndex);
              else document.getElementById("module-assessment")?.scrollIntoView({ behavior: "smooth" });
            }
          }}
        >
          {isSlidePresentation && state.mode !== "presenter" && presenterSlide < slideCount - 1 ? (
            <>
              Next <ChevronRight />
            </>
          ) : state.completedLessons.includes(lesson.id) ? (
            <>
              {module.lessons.length > 1 ? "Next lesson" : "Continue"}{" "}
              <ChevronRight />
            </>
          ) : (
            <>
              Complete assessment <Check />
            </>
          )}
        </button>
      </footer>
      <ModuleNav
        course={course}
        open={nav}
        close={() => setNav(false)}
        select={(i, lessonId) => {
          selectLesson(i, lessonId);
          setNav(false);
        }}
        completed={state.completed}
        completedLessons={state.completedLessons}
        activeLessonId={lesson.id}
        bookmarks={state.bookmarks}
        canAccess={canAccess}
        reset={resetProgress}
        returnFocusRef={navTriggerRef}
        resources={course.id === "professional-customer-service-client-experience" ? customerServiceResources : []}
        resourceDocuments={state.resourceDocuments}
        openResource={(id) => { setActiveResourceId(id); setNav(false); }}
      />
    </div>
  );

  function WorkedExample() {
    const e = lesson.workedExample;
    return (
      <section id="module-worked-example" className="worked">
        <p className="eyebrow">Worked example</p>
        <h2>{e.title}</h2>
        <p>{e.scenario}</p>
        <div className="calculation">
          {e.calculation?.map((x) => (
            <div key={x}>{x}</div>
          ))}
        </div>
        <p className="decision">
          <b>Management decision</b>
          {e.decision}
        </p>
      </section>
    );
  }
  function VisualCard() {
    const visual = lesson.visual;
    const semanticVisual = course.id === "professional-customer-service-client-experience"
      ? customerServiceVisualByLesson(lesson.id)
      : undefined;
    return (
      <figure
        className={`visual visual-${visual.type}`}
        aria-labelledby={`visual-${lesson.id}`}
      >
        {semanticVisual ? (
          <CustomerServiceVisual visual={semanticVisual} />
        ) : visual.source ? (
          <div className="visual-frame" data-ratio={visual.aspectRatio || "16:9"}>
            <img
              src={visual.source}
              alt={visual.altText}
              loading="lazy"
              decoding="async"
              width="1200"
              height="675"
            />
            <span>{visual.type}</span>
          </div>
        ) : (
          <GenericLessonVisual visual={visual} />
        )}
        <figcaption>
          <p className="eyebrow">Visual teaching element · {visual.type}</p>
          <h2 id={`visual-${lesson.id}`}>{visual.title}</h2>
          <p>
            <b>Why this matters:</b> {visual.purpose}
          </p>
          <p>{visual.description}</p>
          {visual.animation && (
            <details>
              <summary>Reveal guidance</summary>
              <p>
                {visual.animation} Motion is removed when the learner requests
                reduced motion.
              </p>
            </details>
          )}
        </figcaption>
      </figure>
    );
  }
}

function AcademyHome({
  academy,
  overallProgress,
  openCourse,
  openProgram,
}: {
  academy: AcademyState;
  overallProgress: number;
  openCourse: (id: string) => void;
  openProgram: (id: string) => void;
}) {
  const progress = (course: Course) =>
    lessonProgress(course, academy.courses[course.id]?.completedLessons ?? []);
  const complete = (course: Course) =>
    requiredLessonIds(course).length > 0 &&
    progress(course) === 100 &&
    (course.id !== "project-finance-management" ||
      academy.courses[course.id]?.finalExamPassed);
  const continuing = catalogCourses.filter(
    (course) =>
      course.availability !== "research" &&
      progress(course) > 0 &&
      !complete(course),
  );
  const recent = academy.recentlyViewed
    .map((id) => catalogCourses.find((course) => course.id === id))
    .filter(Boolean) as Course[];
  const completed = catalogCourses.filter(complete);
  const programProgress = (program: Program) => {
    const total = program.courses.reduce((sum, c) => sum + requiredLessonIds(c).length, 0);
    const done = program.courses.reduce((sum, c) => sum + (academy.courses[c.id]?.completedLessons.length ?? 0), 0);
    return total ? Math.round((done / total) * 100) : 0;
  };
  const categories = [
    "Finance",
    "Business Management",
    "Leadership",
    "Project Delivery",
    "Sales & Marketing",
    "Operations",
    "People",
    "Legal",
    "Digital & AI",
    "Strategy",
  ];
  return (
    <main className="academy-home">
      <header>
        <div className="brand">
          <span>P</span>Pulkra Academy
        </div>
        <p>Professional learning for owners who build, lead and grow.</p>
      </header>
      <section className="academy-hero">
        <div>
          <p className="eyebrow light">Welcome to Pulkra Academy</p>
          <h1>
            Learn the business.
            <br />
            <em>Lead with evidence.</em>
          </h1>
          <p>
            Choose a practical course, apply each lesson to your business and
            return exactly where you stopped.
          </p>
        </div>
        <div className="overall-progress">
          <span>{overallProgress}%</span>
          <div>
            <b>Overall learning progress</b>
            <small>
              {academyCourses.reduce(
                (sum, course) =>
                  sum +
                  (academy.courses[course.id]?.completedLessons.length ?? 0),
                0,
              )}{" "}
              of{" "}
              {academyCourses.reduce(
                (sum, course) => sum + requiredLessonIds(course).length,
                0,
              )}{" "}
              lessons completed
            </small>
          </div>
        </div>
      </section>
      <HomeShelf title="Continue learning">
        {continuing.length ? (
          <div className="course-grid compact">
            {continuing.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                progress={progress(course)}
                completed={false}
                open={() => openCourse(course.id)}
              />
            ))}
          </div>
        ) : (
          <div className="empty-shelf">
            <BookOpen />
            <p>
              Start a featured course and your next lesson will appear here.
            </p>
          </div>
        )}
      </HomeShelf>
      <HomeShelf title="Featured courses">
        <div className="course-grid">
          {academyPrograms
            .filter((program) => program.featured)
            .map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                progress={programProgress(program)}
                open={() => openProgram(program.id)}
              />
            ))}
          {catalogCourses
            .filter((course) => course.featured)
            .map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                progress={progress(course)}
                completed={complete(course)}
                open={() => openCourse(course.id)}
              />
            ))}
        </div>
      </HomeShelf>
      <HomeShelf title="Course categories">
        <div className="category-row">
          {categories.map((category) => (
            <span key={category}>
              {category}
              <b>
                {catalogCourses.filter((course) => course.category === category).length +
                  academyPrograms.filter((program) => program.category === category).length}
              </b>
            </span>
          ))}
        </div>
      </HomeShelf>
      <HomeShelf title="Recently viewed">
        {recent.length ? (
          <div className="course-grid compact">
            {recent.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                progress={progress(course)}
                completed={complete(course)}
                open={() => openCourse(course.id)}
              />
            ))}
          </div>
        ) : (
          <div className="empty-shelf">
            <Clock />
            <p>Your recently opened courses will appear here.</p>
          </div>
        )}
      </HomeShelf>
      <HomeShelf title="Completed courses">
        <div className="completed-shelf">
          {completed.length ? (
            completed.map((course) => (
              <button key={course.id} onClick={() => openCourse(course.id)}>
                <Award />
                <span>
                  <b>{course.title}</b>
                  <small>Completed · Review course</small>
                </span>
              </button>
            ))
          ) : (
            <p>
              Completed courses will appear here with their certificate status.
            </p>
          )}
        </div>
      </HomeShelf>
      <footer>
        <div className="brand">
          <span>P</span>Pulkra Academy
        </div>
        <p>
          More pathways are planned across leadership, projects, marketing,
          operations, people, legal, digital business and strategic growth.
        </p>
      </footer>
    </main>
  );
}
function HomeShelf({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="home-shelf">
      <div className="shelf-title">
        <div>
          <h2>{title}</h2>
          {subtitle ? <p className="shelf-subtitle">{subtitle}</p> : null}
        </div>
        <span />
      </div>
      {children}
    </section>
  );
}
function ProgramCard({
  program,
  progress,
  open,
}: {
  program: Program;
  progress: number;
  open: () => void;
}) {
  const builtCourses = program.courses.filter((c) => c.availability !== "research");
  const totalLessons = program.courses.reduce((sum, c) => sum + requiredLessonIds(c).length, 0);
  return (
    <article
      className="course-card"
      style={{ "--course-accent": program.accent } as React.CSSProperties}
    >
      <div className="course-cover">
        {program.image ? (
          <img src={program.image} alt="" loading="lazy" />
        ) : (
          <div className="course-cover-fallback">
            <Layers />
            <b>{program.shortTitle}</b>
          </div>
        )}
        <span>{program.category}</span>
      </div>
      <div className="course-card-body">
        <p className="eyebrow">Programme · {program.courses.length} courses</p>
        <h3>{program.title}</h3>
        <p>{program.description}</p>
        <div className="course-facts">
          <span>
            <BookOpen />
            {builtCourses.length} of {program.courses.length} courses live ·{" "}
            {totalLessons} lessons
          </span>
          <span>
            <Clock />
            {program.duration}
          </span>
        </div>
        <div className="card-progress" aria-label={`${progress}% complete`}>
          <i style={{ width: `${progress}%` }} />
          <span>{progress}% complete</span>
        </div>
        <button onClick={open}>
          Open programme
          <ChevronRight />
        </button>
      </div>
    </article>
  );
}
const PROGRAM_LEVEL_GROUPS: { level: string; title: string; subtitle: string }[] = [
  { level: "100", title: "Level 100", subtitle: "Year 1 · Foundations" },
  { level: "200", title: "Level 200", subtitle: "Year 2 · Positioning & Growth Systems" },
  { level: "300", title: "Level 300", subtitle: "Year 3 · Strategy & Economics" },
  { level: "400", title: "Level 400", subtitle: "Year 3–4 · Synthesis & Capstone" },
];

function ProgramView({
  program,
  academy,
  openCourse,
  back,
}: {
  program: Program;
  academy: AcademyState;
  openCourse: (id: string) => void;
  back: () => void;
}) {
  const progress = (course: Course) =>
    lessonProgress(course, academy.courses[course.id]?.completedLessons ?? []);
  const complete = (course: Course) =>
    requiredLessonIds(course).length > 0 && progress(course) === 100;
  return (
    <main className="academy-home">
      <header>
        <div className="brand">
          <span>P</span>Pulkra Academy
        </div>
        <button className="text-button" onClick={back}>
          <ChevronLeft /> Back to Academy
        </button>
      </header>
      <section className="academy-hero">
        <div>
          <p className="eyebrow light">{program.category}</p>
          <h1>{program.title}</h1>
          <p>{program.description}</p>
        </div>
      </section>
      {PROGRAM_LEVEL_GROUPS.map((group) => {
        const courses = program.courses.filter((c) => c.level.replace(/\D/g, "") === group.level);
        if (courses.length === 0) return null;
        return (
          <HomeShelf key={group.level} title={group.title} subtitle={group.subtitle}>
            <div className="program-course-grid">
              {courses.map((course) => (
                <CompactCourseCard
                  key={course.id}
                  course={course}
                  progress={progress(course)}
                  completed={complete(course)}
                  open={() => openCourse(course.id)}
                />
              ))}
            </div>
          </HomeShelf>
        );
      })}
    </main>
  );
}
function CompactCourseCard({
  course,
  progress,
  completed,
  open,
}: {
  course: Course;
  progress: number;
  completed: boolean;
  open: () => void;
}) {
  const research = course.availability === "research";
  const [code, name] = course.title.includes(" — ")
    ? course.title.split(" — ")
    : [course.shortTitle, course.title];
  return (
    <article
      className={`compact-course-card${research ? " is-research" : ""}`}
      style={{ "--course-accent": course.accent } as React.CSSProperties}
    >
      <div className="compact-course-card-head">
        <div className="compact-course-card-icon">
          {hasCourseCoverIcon(course.id) ? (
            <CourseCoverIcon courseId={course.id} />
          ) : (
            <BookOpen />
          )}
        </div>
        <div>
          <p className="compact-course-card-code">{code}</p>
          <h4>{name}</h4>
        </div>
      </div>
      <p>{course.description}</p>
      <div className="compact-course-card-footer">
        <div className="compact-course-card-meta">
          <span>
            <BookOpen />
            {research && course.production
              ? `${course.modules.length} modules`
              : research
                ? "Pending"
                : `${course.modules.length} modules`}
          </span>
          <span>
            <Clock />
            {course.duration}
          </span>
        </div>
        <div
          className="card-progress"
          aria-label={research ? "Research in progress" : `${progress}% complete`}
        >
          <i style={{ width: research ? "0%" : `${progress}%` }} />
          <span>
            {research
              ? course.production?.label ?? "Research in progress"
              : completed
                ? "Completed"
                : `${progress}% complete`}
          </span>
        </div>
        <button onClick={open}>
          {research
            ? "View framework"
            : completed
              ? "Review course"
              : progress
                ? "Continue course"
                : "Start course"}
          {!research && <ChevronRight />}
        </button>
      </div>
    </article>
  );
}
function CourseCard({
  course,
  progress,
  completed,
  open,
}: {
  course: Course;
  progress: number;
  completed: boolean;
  open: () => void;
}) {
  const research = course.availability === "research";
  const cover = course.image ?? course.modules[0]?.lessons[0]?.visual.source;
  return (
    <article
      className="course-card"
      style={{ "--course-accent": course.accent } as React.CSSProperties}
    >
      <div className="course-cover">
        {cover ? (
          <img src={cover} alt="" loading="lazy" />
        ) : (
          <div className="course-cover-fallback">
            <BookOpen />
            <b>{course.shortTitle}</b>
          </div>
        )}
        <span>{course.category}</span>
      </div>
      <div className="course-card-body">
        <p className="eyebrow">
          {course.level} · {course.discipline}
        </p>
        <h3>{course.title}</h3>
        <p>{course.description}</p>
        <div className="course-facts">
          <span>
            <BookOpen />
            {research && course.production
              ? `${course.modules.length} modules · ${requiredLessonIds(course).length} lessons`
              : research
                ? "Modules pending research"
                : `${course.modules.length} modules`}
          </span>
          <span>
            <Clock />
            {course.duration}
          </span>
        </div>
        <div
          className="card-progress"
          aria-label={
            research ? "Research in progress" : `${progress}% complete`
          }
        >
          <i style={{ width: research ? "0%" : `${progress}%` }} />
          <span>
            {research
              ? course.production?.label ?? "Research in progress"
              : completed
                ? "Completed"
                : `${progress}% complete`}
          </span>
        </div>
        <button onClick={open}>
          {research
            ? "View course framework"
            : completed
              ? "Review course"
              : progress
                ? "Continue course"
                : "Start course"}
          {!research && <ChevronRight />}
        </button>
      </div>
    </article>
  );
}
function CourseFramework({
  course,
  close,
}: {
  course: Course;
  close: () => void;
}) {
  const scaffold = course.scaffold;
  if (!scaffold)
    return (
      <main className="orientation course-framework">
        <div className="brand">
          <span>P</span>Pulkra Academy
        </div>
        <section>
          <button className="text-button" onClick={close}>
            <ChevronLeft /> Back to Academy
          </button>
          <p className="eyebrow light">PA-CS-201 · Course production review</p>
          <h1>{course.title}</h1>
          <p>
            The approved curriculum contains {course.modules.length} modules
            and {course.modules.flatMap((module) => module.lessons).length}{" "}
            lessons. Course access remains restricted while content,
            assessments and supporting resources are completed and reviewed.
          </p>
          {course.modules.map((module) => (
            <section className="hero-card" key={module.id}>
              <p className="eyebrow">Module {module.number}</p>
              <h2>{module.title}</h2>
              {course.production?.moduleStatuses[module.id] && (
                <p className="module-production-status">
                  <b>{course.production.moduleStatuses[module.id].label}</b>
                  {course.production.moduleStatuses[module.id].detail && (
                    <><br />{course.production.moduleStatuses[module.id].detail}</>
                  )}
                </p>
              )}
              <ol>
                {module.lessons.map((lesson) => (
                  <li key={lesson.id}>
                    {lesson.title} · {lesson.duration} min
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </section>
      </main>
    );
  const groups = [
    ["Course overview", scaffold.overviewSections],
    ["Module structure · Module 1 to Module N", scaffold.moduleSectionTemplate],
    ["Practical workshops", scaffold.workshopSections],
    ["Assessment framework", scaffold.assessmentSections],
    ["Student resources", scaffold.studentResourceSections],
    ["Instructor resources", scaffold.instructorResourceSections],
    ["Appendices", scaffold.appendixSections],
  ] as const;
  return (
    <main className="orientation course-framework">
      <div className="brand">
        <span>P</span>Pulkra Academy
      </div>
      <section>
        <button className="text-button" onClick={close}>
          <ChevronLeft /> Back to Academy
        </button>
        <p className="eyebrow light">
          {scaffold.courseCode} · Course framework
        </p>
        <h1>{course.title}</h1>
        <p>
          This professional course framework is ready for researched content.
        </p>
        <div className="orientation-grid">
          <article>
            <b>Level</b>
            <p>{course.level}</p>
          </article>
          <article>
            <b>Category</b>
            <p>{course.category}</p>
          </article>
          <article>
            <b>Duration</b>
            <p>{course.duration}</p>
          </article>
          <article>
            <b>Prerequisite</b>
            <p>{scaffold.prerequisites.join(", ")}</p>
          </article>
        </div>
        {groups.map(([title, items]) => (
          <section className="hero-card" key={title}>
            <h2>{title}</h2>
            <ul>
              {items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        ))}
      </section>
    </main>
  );
}
function Orientation({
  course,
  onComplete,
}: {
  course: Course;
  onComplete: () => void;
}) {
  const finance = course.id === "project-finance-management";
  return (
    <main className="orientation">
      <div className="brand">
        <span>P</span>Pulkra Academy
      </div>
      <section>
        <InstructorLayer
          cues={getOrientationInstructorCues(course)}
          contextKey={`${course.id}-orientation`}
        />
        <p className="eyebrow light">{course.title} · Before Module 1</p>
        <h1>How to learn here</h1>
        <p>
          This is an operating course, not passive reading. Bring one real or
          realistic business situation and convert each lesson into evidence you
          can use.
        </p>
        <div id="orientation-method" className="orientation-grid">
          <article>
            <b>01 · Learn</b>
            <p>
              Study the explanation, worked GHS example and visual decision
              path.
            </p>
          </article>
          <article>
            <b>02 · Apply</b>
            <p>
              Complete the exercise and use the practical controls on your own
              figures.
            </p>
          </article>
          <article>
            <b>03 · Demonstrate</b>
            <p>Score at least 70% in each module to unlock the next one.</p>
          </article>
          <article>
            <b>04 · Certify</b>
            <p>
              {finance
                ? "Complete the capstone and pass the 45-question final examination at 80%."
                : "Complete every module assessment and produce the practical owner-control exercises."}
            </p>
          </article>
        </div>
        <aside>
          <b>Controls</b>
          <p>
            Bookmarks and position save separately for this course. Presenter
            mode uses Arrow keys, Page Up/Down and Space. Fullscreen and print
            controls are available throughout. Your figures remain on this
            device.
          </p>
        </aside>
        <button className="primary" onClick={onComplete}>
          Start Module 1 <ChevronRight />
        </button>
      </section>
    </main>
  );
}
function StandardCourseCompletion({
  course,
  name,
  setName,
}: {
  course: Course;
  name: string;
  setName: (value: string) => void;
}) {
  return (
    <section className="completion">
      <Award />
      <p className="eyebrow">Course completed</p>
      <h2>{course.title}</h2>
      <p>
        You completed every module and assessment in this Pulkra Academy
        pathway.
      </p>
      <label className="certificate-name">
        Learner name
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Enter your full name"
        />
      </label>
      <div className="certificate">
        <small>PULKRA ACADEMY · PROFESSIONAL LEARNING</small>
        <h3>Certificate of Completion</h3>
        <p>This certifies that</p>
        <b>{name || "Learner name"}</b>
        <p>successfully completed</p>
        <b>{course.title}</b>
        <span>
          {course.level} · {course.discipline}
        </span>
        <footer>
          <i>
            PA-{new Date().getFullYear()}-{course.id.slice(0, 3).toUpperCase()}
          </i>
          <i>{new Date().toLocaleDateString("en-GH", { dateStyle: "long" })}</i>
        </footer>
      </div>
      <button onClick={() => window.print()}>
        <Printer /> Print certificate
      </button>
    </section>
  );
}
function ModuleNav({
  course,
  open,
  close,
  select,
  completed,
  completedLessons,
  activeLessonId,
  bookmarks,
  canAccess,
  reset,
  returnFocusRef,
  resources,
  resourceDocuments,
  openResource,
}: {
  course: Course;
  open: boolean;
  close: () => void;
  select: (i: number, lessonId: string) => void;
  completed: string[];
  completedLessons: string[];
  activeLessonId: string;
  bookmarks:LessonBookmark[];
  canAccess: (i: number) => boolean;
  reset: () => void;
  returnFocusRef:React.RefObject<HTMLButtonElement|null>;
  resources: typeof customerServiceResources;
  resourceDocuments: LearningState["resourceDocuments"];
  openResource: (id: string) => void;
}) {
  const dialogRef=useRef<HTMLElement>(null);const closeRef=useRef<HTMLButtonElement>(null);
  useEffect(()=>{if(!open)return;const previous=document.activeElement as HTMLElement|null;const returnTarget=returnFocusRef.current;closeRef.current?.focus();const trap=(event:KeyboardEvent)=>{if(event.key==='Escape'){event.preventDefault();close();return}if(event.key!=='Tab')return;const focusable=Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input,textarea,select,[tabindex]:not([tabindex="-1"])')??[]);if(!focusable.length)return;const first=focusable[0],last=focusable[focusable.length-1];if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus()}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus()}};document.addEventListener('keydown',trap);return()=>{document.removeEventListener('keydown',trap);(returnTarget??previous)?.focus()}},[open,close,returnFocusRef]);
  if(!open)return null;
  const saved=bookmarks.flatMap(bookmark=>{const moduleIndex=course.modules.findIndex(item=>item.id===bookmark.moduleId);const module=course.modules[moduleIndex];const lesson=module?.lessons.find(item=>item.id===bookmark.lessonId);return module&&lesson?[{moduleIndex,module,lesson}]:[]});
  return (
    <div className="nav-overlay open">
      <button className="scrim" onClick={close} aria-label="Close navigation" />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Course module navigation"
      >
        <header>
          <div>
            <p className="eyebrow">Course map</p>
            <h2>{course.shortTitle}</h2>
          </div>
          <button
            ref={closeRef}
            className="icon"
            onClick={close}
            aria-label="Close module navigation"
          >
            <X />
          </button>
        </header>
        {saved.length>0&&<section className="saved-lessons" aria-labelledby="saved-lessons-title"><p className="eyebrow" id="saved-lessons-title">Saved lessons</p>{saved.map(({moduleIndex,module,lesson})=><button key={lesson.id} onClick={()=>select(moduleIndex,lesson.id)}><Bookmark/><span><b>{lesson.title}</b><small>{course.shortTitle} · {module.title}</small></span></button>)}</section>}
        {course.modules.map((module, moduleIndex) => {
          const unlocked = canAccess(moduleIndex);
          return (
            <section className="module-nav-group" key={module.id}>
              <button
                className="module-link"
                onClick={() => select(moduleIndex, module.lessons[0].id)}
                disabled={!unlocked}
              >
                <span>
                  {completed.includes(module.id) ? (
                    <Check />
                  ) : unlocked ? (
                    String(moduleIndex + 1).padStart(2, "0")
                  ) : (
                    <Lock />
                  )}
                </span>
                <div>
                  <b>{module.title}</b>
                  <small>
                    {unlocked
                      ? module.promise
                      : "Complete the previous module to unlock."}
                  </small>
                </div>
              </button>
              <div className="lesson-links">
                {module.lessons.map((lesson, lessonIndex) => (
                  <button
                    key={lesson.id}
                    className={activeLessonId === lesson.id ? "active" : ""}
                    onClick={() => select(moduleIndex, lesson.id)}
                    disabled={!unlocked}
                    aria-current={
                      activeLessonId === lesson.id ? "page" : undefined
                    }
                  >
                    <span>
                      {completedLessons.includes(lesson.id) ? (
                        <Check />
                      ) : (
                        `${moduleIndex + 1}.${lessonIndex + 1}`
                      )}
                    </span>
                    {lesson.title}
                  </button>
                ))}
              </div>
            </section>
          );
        })}
        {resources.length > 0 && (
          <section className="resource-index" aria-labelledby="resource-index-title">
            <p className="eyebrow" id="resource-index-title">Course resources</p>
            {resources.map((resource) => {
              const savedResource = resourceDocuments[resource.id];
              return (
                <button key={resource.id} onClick={() => openResource(resource.id)}>
                  <Files />
                  <span>
                    <b>{resource.title}</b>
                    <small>{savedResource?.status ?? "not-started"}{savedResource?.updatedAt ? ` - saved ${new Date(savedResource.updatedAt).toLocaleDateString()}` : ""}</small>
                  </span>
                </button>
              );
            })}
          </section>
        )}
        <button className="text-button reset-progress" onClick={reset}>
          <RotateCcw /> Reset this course progress
        </button>
      </aside>
    </div>
  );
}
export default App;
